import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import ColoredSectionContainer from 'components/no-auth/common/ColoredSectionContainer';
import { PromoPageContainer } from 'components/no-auth/common/PromoPageContainer/PromoPageContainer';
import HeadingSection from 'components/no-auth/common/sections/HeadingSection';
import { HeroSection } from 'components/no-auth/common/sections/HeroSection';
import NotAuthBasePage from 'components/no-auth/NotAuthBasePage';

import { HeroExtraBlock } from '../components/no-auth/acceptInvite/HeroExtraBlock';
import DownloadAppPopup from '../components/no-auth/common/Popups/DownloadAppPopup';
import ErrorPopup from '../components/no-auth/common/Popups/ErrorPopup';
import QRCodePopup from '../components/no-auth/common/Popups/QRCodePopup';
import FaqSection from '../components/no-auth/common/sections/FaqSection';
import { TilesSection } from '../components/no-auth/common/sections/TilesSection';
import TrustpilotSection from '../components/no-auth/common/sections/TrustpilotSection';
import PromoPopup from '../components/no-auth/popup/PromoPopup';
import { NO_AUTH_LOGIN_REGISTRATION_FORM_NAME } from '../constants/formName';
import { createInvitedUsersData } from '../helpers/api';
import { getLangCountryPrefix } from '../helpers/utils';
import { useForm } from '../hooks/selectors';

const AcceptInvitePage = (props) => {
    const [inviteCode, setInviteCode] = useState('');
    const popupRef = useRef(null);
    const form = useForm(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME);

    const noteText = props.heroNote;
    const prefix = getLangCountryPrefix(props.lang, props.country);

    const addPrefixToLinks = (text, prefix) => {
        return text?.replace(/<a\s+href="\/(.*?)">(.*?)<\/a>/g, (_, path, linkText) => `<a href="${prefix}/${path}">${linkText}</a>`);
    };

    const updatedNoteText = addPrefixToLinks(noteText, prefix);

    useEffect(() => {
        let pathname = location.pathname;
        let arr = pathname.split('/');
        let code = arr.pop();
        setInviteCode(code);
    }, []);

    const getPureValue = (prefix) => {
        return prefix.replace(/\D+/g, '');
    };

    const clickHandler = async () => {
        const purePhonePrefix = getPureValue(form?.fields?.phone_prefix?.value);
        const purePhoneNumber = form?.fields?.phone_number?.value;
        const phoneCountry = form?.fields?.phone_country?.value;
        const phoneNumber = purePhonePrefix + purePhoneNumber;
        const response = await createInvitedUsersData(phoneNumber, inviteCode, props.lang);

        if (response.code === 0) {
            if (props.isMobileDevice) {
                popupRef.current?.open(<DownloadAppPopup popupRef={popupRef} />);
            } else {
                popupRef.current?.open(<QRCodePopup popupRef={popupRef} />);
            }
            const invitedUserData = {
                prefix: purePhonePrefix,
                phone: purePhoneNumber,
                country: response.countryA2.toLowerCase() || phoneCountry,
            };
            sessionStorage?.setItem('invitedUserData', JSON.stringify(invitedUserData));
        } else {
            popupRef.current?.open(<ErrorPopup popupRef={popupRef} text={response?.message} btnText={'Ok'} />);
        }
    };

    const isBtnDisabled = !(form?.fields?.phone_number?.value && (form?.fields?.phone_prefix?.value + form?.fields?.phone_number?.value).length >= 6);

    return (
        <>
            <PromoPageContainer bgColor="eggshell03" noIndent>
                {props.heroTitle && (
                    <HeroSection
                        title={props.heroTitle}
                        text={props.heroText}
                        bgImg={props.heroBgImg}
                        img={props.heroImg}
                        data={props.heroData}
                        note={updatedNoteText}
                        horizontalLayout
                        hasQrCode
                        extraComponent={
                            <HeroExtraBlock
                                currentCountry={props.country.toLowerCase()}
                                countries={props.availableCountries}
                                labelText={props.inputLabel}
                                btnTitle={props.heroData?.extra_button?.title}
                                isMobileDevice={props.isMobileDevice}
                                disabledBtn={isBtnDisabled}
                                onExtraButtonClick={clickHandler}
                                searchPlaceholder={props.searchPlaceholder}
                                searchFullPlaceholder={props.searchFullPlaceholder}
                                searchModalTitle={props.searchModalTitle}
                                searchNoResult={props.searchNoResult}
                            />
                        }
                    />
                )}

                <ColoredSectionContainer>
                    {props.headline1Title && <HeadingSection title={props.headline1Title} text={props.headline1Text} />}

                    {props.lang && <TrustpilotSection lang={props.lang} />}

                    {props.headline2Title && <HeadingSection title={props.headline2Title} text={props.headline2Text} />}
                    {props.tiles && <TilesSection data={props.tiles} lang={props.lang} country={props.country} loadingLazy grayText />}
                </ColoredSectionContainer>

                {props.faqTitle && props.questions && <FaqSection title={props.faqTitle} questions={props.questions} noTopPadding />}
            </PromoPageContainer>
            <PromoPopup ref={popupRef} />
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        heroTitle: state.extra?.strapiApi?.hero_block?.title,
        heroText: state.extra?.strapiApi?.hero_block?.text,
        heroData: state.extra?.strapiApi?.hero_block,
        heroBgImg: state.extra?.strapiApi?.hero_block?.bgImg,
        heroImg: state.extra?.strapiApi?.hero_block?.img,
        heroNote: state.extra?.strapiApi?.hero_block?.note,

        inputLabel: state.extra?.strapiApi?.input_label,

        headline1Title: state.extra?.strapiApi?.headline_1?.title,
        headline1Text: state.extra?.strapiApi?.headline_1?.text,

        headline2Title: state.extra?.strapiApi?.headline_2?.title,
        headline2Text: state.extra?.strapiApi?.headline_2?.text,

        tiles: state.extra?.strapiApi?.tiles_section,

        faqTitle: state.extra?.strapiApi?.faq?.title,
        questions: state.extra?.strapiApi?.faq?.questions,

        isMobileDevice: state.extra?.strapiApi?.isMobileDevice,

        availableCountries: state.extra?.strapiApi?.availableCountries,

        searchPlaceholder: state.extra?.strapiApi?.search_placeholder,
        searchFullPlaceholder: state.extra?.strapiApi?.search_full_placeholder,
        searchModalTitle: state.extra?.strapiApi?.search_modal_title,
        searchNoResult: state.extra?.strapiApi?.search_no_result,

        lang: state.extra.noAuthFooter?.language?.lang,
        country: state.extra.noAuthFooter?.language?.country,
    };
};

export default connect(mapStateToProps)(NotAuthBasePage(AcceptInvitePage));
